import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CollectionsIcon from '@mui/icons-material/Collections';

function Images() {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [canUpload, setCanUpload] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchAuthSession().then(session => {
            const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
            setCanUpload(groups.includes('image_management'));
        });
    }, []);

    const isListActive = pathname === '/images' || pathname === '/images/list';
    const isUploadActive = pathname === '/images/upload';

    const tabs = [
        { label: t('images.library'), to: '/images/list', icon: <CollectionsIcon />, active: isListActive },
        ...(canUpload ? [{ label: t('images.upload'), to: '/images/upload', icon: <CloudUploadIcon />, active: isUploadActive }] : []),
    ];

    const activeTabIndex = tabs.findIndex(t => t.active);

    if (isMobile) {
        return (
            <Box sx={{ pt: 2 }}>
                <Tabs
                    value={activeTabIndex === -1 ? 0 : activeTabIndex}
                    onChange={(_, i) => navigate(tabs[i].to)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 2,
                        '& .MuiTab-root': { minWidth: 0, px: 2, fontSize: '0.8rem' },
                    }}
                >
                    {tabs.map((t) => (
                        <Tab key={t.to} label={t.label} icon={t.icon} iconPosition="start" />
                    ))}
                </Tabs>
                <Paper elevation={2} sx={{ p: 2 }}>
                    <Outlet />
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 250 }} role="navigation" aria-label="images submenu">
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton
                                component={NavLink}
                                to="/images/list"
                                selected={isListActive}
                                sx={{ '&.active': { backgroundColor: 'action.selected' } }}
                            >
                                <ListItemIcon><CollectionsIcon /></ListItemIcon>
                                <ListItemText primary={t('images.library')} />
                            </ListItemButton>
                        </ListItem>
                        {canUpload && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={NavLink}
                                    to="/images/upload"
                                    selected={isUploadActive}
                                    sx={{ '&.active': { backgroundColor: 'action.selected' } }}
                                >
                                    <ListItemIcon><CloudUploadIcon /></ListItemIcon>
                                    <ListItemText primary={t('images.upload')} />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </List>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Paper elevation={4} sx={{ p: 2, mt: 2, textAlign: 'left' }}>
                        <Outlet />
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
}

export default Images;
